/**
 * An attribute pill.
 *
 * `register` is the part that matters. A vendor amenity, one of our own lane
 * names, a live status and a term issued by a naming body are four different
 * KINDS of claim, and when they share a style a reader has to read each one to
 * find out which they are looking at. The register changes the pill's shape and
 * case; the variant changes its tone. Both are closed sets.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import {
  CRC_ACCENT,
  CRC_R_INSET,
  CRC_R_PILL,
  CRC_R_TILE,
  CRC_TONE_COLOR,
} from "./cresultcard.tokens";
import {
  CresultcardChipRegister,
  CresultcardChipVariant,
  CresultcardTone,
} from "./cresultcard.types";

const CHIP_GLYPH_SIZE = 11;
const CHIP_GLYPH_STROKE = 1.25;

const VARIANT_STYLE: Record<CresultcardChipVariant, CSSProperties> = {
  [CresultcardChipVariant.Outline]: {
    color: "rgba(244,253,250,0.66)",
    backgroundColor: "rgba(255,255,255,0.035)",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
  },
  [CresultcardChipVariant.Accent]: {
    color: CRC_ACCENT,
    backgroundColor: "rgba(102,254,193,0.09)",
    boxShadow: "inset 0 0 0 1px rgba(102,254,193,0.24)",
  },
  [CresultcardChipVariant.Caution]: {
    color: CRC_TONE_COLOR[CresultcardTone.Caution],
    backgroundColor: "rgba(242,159,87,0.09)",
    boxShadow: "inset 0 0 0 1px rgba(242,159,87,0.26)",
  },
  [CresultcardChipVariant.Negative]: {
    color: CRC_TONE_COLOR[CresultcardTone.Negative],
    backgroundColor: "rgba(255,110,132,0.09)",
    boxShadow: "inset 0 0 0 1px rgba(255,110,132,0.26)",
  },
  [CresultcardChipVariant.Solid]: {
    color: "rgba(221,244,237,0.5)",
    backgroundColor: "rgba(0,0,0,0.35)",
    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.05)",
  },
};

/** The register owns shape and case; it never owns colour. */
const REGISTER_STYLE: Record<CresultcardChipRegister, CSSProperties> = {
  [CresultcardChipRegister.Attribute]: {
    borderRadius: CRC_R_PILL,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.01em",
  },
  [CresultcardChipRegister.Lane]: {
    borderRadius: CRC_R_INSET,
    fontSize: 8.5,
    fontWeight: 720,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  [CresultcardChipRegister.Status]: {
    borderRadius: CRC_R_PILL,
    fontSize: 10,
    fontWeight: 740,
    letterSpacing: "0.02em",
  },
  /**
   * A plate rather than a pill, because an issued term is a citation and not a
   * tag. Its case is the ISSUER's: a scheme's heading carries its own casing and
   * its own `--` subdivisions, and a register that uppercased it would be
   * misquoting the body it came from.
   */
  [CresultcardChipRegister.Authority]: {
    borderRadius: CRC_R_TILE,
    fontSize: 9.5,
    fontWeight: 640,
    letterSpacing: "0.005em",
  },
};

interface CresultcardChipProps {
  readonly children: ReactNode;
  readonly variant?: CresultcardChipVariant;
  readonly register: CresultcardChipRegister;
  readonly icon?: TablerIcon;
  /** The full text when the pill itself is clipped. */
  readonly title?: string;
}

export function CresultcardChip({
  children,
  variant = CresultcardChipVariant.Outline,
  register,
  icon: Glyph,
  title,
}: CresultcardChipProps): ReactElement {
  return (
    <span
      data-el="Cresultcard/Chip/Root"
      data-crc-register={register}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        maxWidth: "100%",
        minWidth: 0,
        paddingBlock: 3,
        paddingInline: 8,
        overflow: "hidden",
        lineHeight: 1.3,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...VARIANT_STYLE[variant],
        ...REGISTER_STYLE[register],
      }}
      title={title}
    >
      {Glyph != null && <Glyph size={CHIP_GLYPH_SIZE} stroke={CHIP_GLYPH_STROKE} />}
      {children}
    </span>
  );
}
